/* ============================================================================
   xa-top-intro.js — TOP page entry-curtain controller
   - Renders a Three.js DNA scene inside the overlay
   - Fades the overlay out after `data-duration` ms (or on click / Skip)
   - Marks the session as "seen" via sessionStorage so the intro plays once
   - The pre-paint inline script in the section already short-circuits when
     a returning visitor or reduced-motion user loads the page.
============================================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'xa_top_intro_seen';
  var FADE_OUT_MS = 700;            // matches transition in xa-top-intro.css
  var overlay = document.getElementById('xa-top-intro');
  var html = document.documentElement;
  if (!overlay) return;

  // Pre-paint script may have already decided to skip — bail.
  if (html.classList.contains('xa-intro-skip')) {
    overlay.parentNode && overlay.parentNode.removeChild(overlay);
    return;
  }

  var duration = parseInt(overlay.getAttribute('data-duration'), 10) || 4500;
  var oncePerSession = overlay.getAttribute('data-once') !== 'false';
  var canvas = overlay.querySelector('.xa-top-intro__canvas');
  var skipBtn = overlay.querySelector('.xa-top-intro__skip');
  var dismissed = false;

  // -------------------- Three.js DNA setup --------------------
  // Falls back gracefully if THREE failed to load (no overlay = page reveals).
  if (!window.THREE) {
    setTimeout(dismiss, 800);
    return;
  }

  var scene = new THREE.Scene();
  var camera = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  );
  camera.position.z = 22;

  var renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    alpha: true
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  function makeSphereTexture() {
    var c = document.createElement('canvas');
    c.width = c.height = 64;
    var ctx = c.getContext('2d');
    var g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0,   'rgba(80, 80, 80, 1)');
    g.addColorStop(0.2, 'rgba(50, 50, 50, 0.8)');
    g.addColorStop(0.5, 'rgba(30, 30, 30, 0.3)');
    g.addColorStop(1,   'rgba(0, 0, 0, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, c.width, c.height);
    return new THREE.CanvasTexture(c);
  }

  // ----- DNA geometry (mirrored from index_hero_js.liquid) -----
  var particles = [];
  var dnaRadius = 4;
  var turnHeight = 12;
  var numTurns = 4;
  var totalHeight = turnHeight * numTurns;
  var numStrandParticles = 6000;
  var rungsPerTurn = 14;
  var strandRandomFactor = 0.6;
  var rungRandomFactor = 0.3;

  for (var i = 0; i < numStrandParticles; i++) {
    var yBase = (i / numStrandParticles) * totalHeight - totalHeight / 2;
    var angle = (yBase / turnHeight) * Math.PI * 2;
    particles.push(
      dnaRadius * Math.cos(angle) + (Math.random() - 0.5) * strandRandomFactor,
      yBase + (Math.random() - 0.5) * strandRandomFactor,
      dnaRadius * Math.sin(angle) + (Math.random() - 0.5) * strandRandomFactor
    );
    particles.push(
      dnaRadius * Math.cos(angle + Math.PI) + (Math.random() - 0.5) * strandRandomFactor,
      yBase + (Math.random() - 0.5) * strandRandomFactor,
      dnaRadius * Math.sin(angle + Math.PI) + (Math.random() - 0.5) * strandRandomFactor
    );
  }

  var totalRungs = numTurns * rungsPerTurn;
  for (var r = 0; r < totalRungs; r++) {
    var ry = (r / totalRungs) * totalHeight - totalHeight / 2;
    var ra = (ry / turnHeight) * Math.PI * 2;
    var x1 = dnaRadius * Math.cos(ra), z1 = dnaRadius * Math.sin(ra);
    var x2 = dnaRadius * Math.cos(ra + Math.PI), z2 = dnaRadius * Math.sin(ra + Math.PI);
    var rungParticles = 70;
    for (var j = 0; j < rungParticles; j++) {
      var t = j / (rungParticles - 1);
      var xx = x1 * (1 - t) + x2 * t;
      var zz = z1 * (1 - t) + z2 * t;
      particles.push(
        xx + (Math.random() - 0.5) * rungRandomFactor,
        ry + (Math.random() - 0.5) * rungRandomFactor,
        zz + (Math.random() - 0.5) * rungRandomFactor
      );
    }
  }

  var originalPositions = new Float32Array(particles);
  var geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(particles, 3));

  var mat = new THREE.PointsMaterial({
    color: 0x333333,
    size: 0.18,
    map: makeSphereTexture(),
    blending: THREE.AdditiveBlending,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
    sizeAttenuation: true
  });

  var dna = new THREE.Points(geom, mat);
  var group = new THREE.Group();
  group.add(dna);
  scene.add(group);

  var clock = new THREE.Clock();
  var rafId = null;
  var startTime = performance.now();

  function animate() {
    rafId = requestAnimationFrame(animate);

    var elapsed = clock.getElapsedTime();

    // Slow autorotate to give the helix a sense of life.
    group.rotation.y = elapsed * 0.25;
    group.rotation.x = Math.sin(elapsed * 0.15) * 0.08;

    // Galactic twist on individual particles for the shimmer.
    var positions = dna.geometry.attributes.position.array;
    var galacticSpeed = 0.1;
    for (var k = 0; k < positions.length; k += 3) {
      var ox = originalPositions[k];
      var oy = originalPositions[k + 1];
      var oz = originalPositions[k + 2];
      var ang = elapsed * galacticSpeed;
      var rx = ox * Math.cos(ang) - oz * Math.sin(ang);
      var rz = ox * Math.sin(ang) + oz * Math.cos(ang);
      var bf = 0.08;
      positions[k]     = rx + Math.sin(elapsed * 0.5 + oy * 0.5) * bf;
      positions[k + 1] = oy + Math.cos(elapsed * 0.3 + ox * 0.5) * bf;
      positions[k + 2] = rz + Math.sin(elapsed * 0.4 + oz * 0.5) * bf;
    }
    dna.geometry.attributes.position.needsUpdate = true;

    renderer.render(scene, camera);
  }
  animate();

  // -------------------- Resize --------------------
  function onResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  }
  window.addEventListener('resize', onResize);

  // -------------------- Dismiss --------------------
  function dismiss() {
    if (dismissed) return;
    dismissed = true;

    overlay.classList.add('is-leaving');

    if (oncePerSession) {
      try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* noop */ }
    }

    setTimeout(function () {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      // Three.js cleanup
      try {
        renderer.dispose();
        geom.dispose();
        mat.dispose();
        if (mat.map) mat.map.dispose();
      } catch (e) { /* noop */ }
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      html.classList.remove('xa-intro-active');
    }, FADE_OUT_MS);
  }

  // Auto-dismiss after the configured duration.
  var autoTimer = setTimeout(dismiss, duration);

  // Click anywhere to skip.
  overlay.addEventListener('click', function () {
    clearTimeout(autoTimer);
    dismiss();
  });

  // Explicit Skip button (also bubbles, but handled defensively).
  if (skipBtn) {
    skipBtn.addEventListener('click', function (ev) {
      ev.stopPropagation();
      clearTimeout(autoTimer);
      dismiss();
    });
  }
})();
